import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { regionApi } from '../api';
import type { AreaDto, CityDto } from '../api';

export const regionQueryKeys = {
  all: ['region'] as const,
  governorates: (filter?: string) => [...regionQueryKeys.all, 'governorates', filter ?? ''] as const,
  cities: (filter?: string) => [...regionQueryKeys.all, 'cities', filter ?? ''] as const,
  governorateCities: (governorateId: string, filter?: string) =>
    [...regionQueryKeys.all, 'governorateCities', governorateId, filter ?? ''] as const,
  governoratesCities: (governorateIds: string[], filter?: string) =>
    [...regionQueryKeys.all, 'governoratesCities', [...governorateIds].sort().join('\0'), filter ?? ''] as const,
  citiesAreas: (cityIds: string[], filter?: string) =>
    [...regionQueryKeys.all, 'citiesAreas', [...cityIds].sort().join('\0'), filter ?? ''] as const,
};

export function useGovernoratesQuery(filter?: string) {
  return useQuery({
    queryKey: regionQueryKeys.governorates(filter),
    queryFn: () => regionApi.getGovernorates({ filter }),
  });
}

export function useCitiesQuery(filter?: string) {
  return useQuery({
    queryKey: regionQueryKeys.cities(filter),
    queryFn: () => regionApi.getCities({ filter }),
  });
}

/** Cities for one or more governorates via GET /app/region/governerates-cities */
export function useCitiesForGovernoratesQuery(governorateIds: string[]) {
  const ids = useMemo(
    () => [...new Set(governorateIds.map((id) => id.trim()).filter(Boolean))],
    [governorateIds]
  );

  const query = useQuery({
    queryKey: regionQueryKeys.governoratesCities(ids),
    queryFn: () => regionApi.getGovernoratesCities({ governorateIds: ids }),
    enabled: ids.length > 0,
    staleTime: 5 * 60 * 1000,
  });

  const data = useMemo((): CityDto[] => {
    const list = query.data ?? [];
    return [...list].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
  }, [query.data]);

  return {
    data,
    isLoading: ids.length > 0 && query.isLoading,
    isError: query.isError,
    error: query.error,
    governorateCount: ids.length,
  };
}

/** Areas for one or more cities via GET /app/region/cities-areas */
export function useAreasForCitiesQuery(cityIds: string[], enabled = true) {
  const ids = useMemo(
    () => [...new Set(cityIds.map((id) => id.trim()).filter(Boolean))],
    [cityIds]
  );

  const query = useQuery({
    queryKey: regionQueryKeys.citiesAreas(ids),
    queryFn: () => regionApi.getCitiesAreas({ cityIds: ids }),
    enabled: enabled && ids.length > 0,
    staleTime: 5 * 60 * 1000,
  });

  const data = useMemo((): AreaDto[] => {
    const list = query.data ?? [];
    return [...list].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
  }, [query.data]);

  return {
    data,
    isLoading: enabled && ids.length > 0 && query.isLoading,
    isError: query.isError,
    error: query.error,
    cityCount: ids.length,
  };
}
